<template>
  <div class="exist-offer-container">
    <a-alert
      v-if="props.message"
      class="exist-offer-container__message"
      :message="props.message"
      showIcon
    />

    <div class="exist-offer-container__header">
      <a-input-search
        v-model="productSearch"
        :placeholder="$t('offers.create_form_placeholders.search_by_name_or_code')"
        @change="onProductSearch()"
        @search="onProductSearch()"
      />

      <a-tree-select
        v-if="props.showCategories"
        v-model="productCategorySearch"
        :treeData="categoriesTree"
        showSearch
        :dropdownStyle="{
          maxHeight: '400px',
          paddingRight: '8px'
        }"
        :placeholder="$t('offers.create_form_fields.category')"
        treeNodeFilterProp="title"
        allowClear
        @change="onProductSearch()"
      />
    </div>

    <a-list
      v-if="dataSource.length"
      :loading="isFetching"
      class="products-list"
    >
      <a-list-item
        :class="{ 'products-list__active-item': item.p_code === $route.query.p_code }"
        v-for="item in dataSource"
        :key="item.p_code"
      >
        <a-list-item-meta>
          <template #title>
            <div class="products-list__item">
              <a-radio
                class="products-list__radio"
                :checked="item.p_code === chosenCode"
                @click="chooseProduct(item)"
              />
              <div>
                <div class="products-list__item__info">
                  <a-badge
                    :count="item.images.length"
                    :number-style="{
                      backgroundColor: 'rgba(230, 247, 255, 1)',
                      color: 'rgba(24, 144, 255, 1)'
                    }"
                  >
                    <a-avatar
                      shape="square"
                      icon="picture"
                      :src="item.images?.[0] ? getFullImagePath(item.images[0]) : undefined"
                      style="cursor: pointer"
                      :size="50"
                      @click="openGallery(item.images)"
                    />
                  </a-badge>

                  <div>
                    <ProductCode
                      :code="item.p_code"
                      :slug="item.slug"
                    />
                    <div>
                      {{ item.name }}
                    </div>
                  </div>
                </div>

                <div class="products-list__item__attrs">
                  <a-tooltip
                    v-for="attr in item.attrs_main.slice(0, 4)"
                    :key="attr.uuid"
                  >
                    <template #title>{{ attr.name }}: {{ attr.value[0]?.label }}</template>
                    <a-tag class="products-list__item__attrs__item">
                      {{
                        attr.value[0]?.label?.length > 20
                          ? `${attr.value[0]?.label.slice(0, 20)}...`
                          : attr.value[0]?.label
                      }}
                    </a-tag>
                  </a-tooltip>
                </div>
              </div>
            </div>
          </template>
        </a-list-item-meta>

        <div class="products-list__content">
          <div>
            <div v-if="item.category_rating">
              #{{ item.category_rating }} {{ $t("offers.in_category") }}
            </div>
            <div
              v-if="item.label"
              :style="{ background: item.label.background_color, color: item.label.font_color }"
              class="products-list__content__label"
            >
              {{ item.label.text }}
            </div>
          </div>
          <div
            class="products-list__actions"
            :class="{ 'products-list__actions--hidden': item.p_code !== chosenCode }"
          >
            <a-button
              icon="eye"
              @click="showProductDetails(item)"
            >
              {{ $t("offers.view_product") }}
            </a-button>
            <a-button
              type="primary"
              icon="plus"
              @click="selectProductForOffer(item)"
            >
              {{ $t("offers.make_offer") }}
            </a-button>
          </div>
        </div>
      </a-list-item>
    </a-list>

    <a-spin v-else-if="isFetching" />
  </div>
</template>

<script lang="ts" setup>
import { inject, Ref, ref, shallowRef } from "vue"

import ProductCode from "@/components/Common/ProductCode"
import type { CategoriesTreeItem, ExistedProduct } from "@/modules/offers/types/catalog"
import { productCatalogSearch } from "@/services/productCatalogService"
import { getFullImagePath } from "@/utils/cdn"

type Props = {
  message?: string
  showCategories?: boolean
}
type Emits = {
  (e: "showDetails" | "selectExistedProduct", product: ExistedProduct): void
}

const props = withDefaults(defineProps<Props>(), { message: "", showCategories: false })
const emit = defineEmits<Emits>()

const showGallery = inject<Function>("showGallery")

const openGallery = (images: Array<string>) => {
  showGallery?.({
    images
  })
}

const productSearch = shallowRef<string>("")
const productCategorySearch = shallowRef<string>()
const searchTimeout = ref<ReturnType<typeof setTimeout>>()
const isFetching = shallowRef<boolean>(false)
const dataSource = ref<ExistedProduct[]>([])

const getProducts = async ({
  q = productSearch.value,
  category = productCategorySearch.value
} = {}) => {
  if (!q && !category) {
    dataSource.value.length = 0
    isFetching.value = false
    return
  }

  try {
    isFetching.value = true
    const query = { q, category_uuid: category }

    const { data } = await productCatalogSearch({ query, pageSize: 50 })

    dataSource.value = data.results
    searchTimeout.value = null
  } catch {
    dataSource.value = []
  } finally {
    isFetching.value = false
  }
}
const onProductSearch = async () => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value)
    searchTimeout.value = null
  }

  isFetching.value = true
  searchTimeout.value = setTimeout(getProducts, 500)
}

// CHOOSE PRODUCT
const chosenCode = ref<string>()

const chooseProduct = (product: ExistedProduct) => {
  if (chosenCode.value === product.p_code) {
    resetChosenProduct()
    return
  }

  chosenCode.value = product.p_code
}
const resetChosenProduct = () => {
  chosenCode.value = undefined
}

// PRODUCT DETAILS
const showProductDetails = (product: ExistedProduct) => {
  emit("showDetails", product)
}
const selectProductForOffer = (product: ExistedProduct) => {
  emit("selectExistedProduct", product)
}

// CATEGORIES

const categoriesTree = inject<Ref<Array<CategoriesTreeItem>>>(
  "categoriesTree",
  shallowRef<Array<CategoriesTreeItem>>([])
)

defineExpose({
  resetChosenProduct,
  getProducts
})
</script>

<style lang="scss" scoped>
.exist-offer-container {
  display: flex;
  flex-flow: column nowrap;
  gap: 16px;

  padding: 16px;

  height: 100%;

  &__message {
    margin-bottom: 8px;
  }

  &__header {
    display: flex;
    gap: 16px;

    & > :nth-child(2) {
      width: 400px;
    }
  }
}

.products-list {
  max-height: 600px;
  overflow: auto;
  padding-right: 8px;

  &__radio {
    height: 100%;
    display: flex;
    align-items: center;

    margin-left: 6px;
  }

  &__item {
    display: flex;
    align-items: center;

    &__info {
      display: flex;
      align-items: center;
      gap: 16px;

      padding-right: 16px;

      & * {
        padding: 0;
      }
    }

    &__attrs {
      display: flex;
      flex-flow: row wrap;
      gap: 12px;

      margin-top: 8px;
      width: 100%;

      &__item {
        color: hsla(0, 0%, 0%, 0.45);
        background: hsla(0, 0%, 100%, 1);
        font-size: 12px;
        line-height: 20px;
        height: 30px;
        padding: 4px 8px;
        margin: 0;
      }
    }
  }

  &__content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 40%;

    &__label {
      width: fit-content;
      padding: 4px 8px;
      border-radius: 8px;
      margin-top: 16px;
    }
  }

  &__actions {
    display: flex;
    flex-flow: column nowrap;
    justify-content: flex-end;
    gap: 8px;

    padding-left: 16px;

    &--hidden {
      opacity: 0;
      pointer-events: none;
    }
  }

  &__active-item {
    background-color: #e6f4ff;
  }
}
</style>
